import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box, Flex, Text, Button, IconButton, Image, SimpleGrid, useToast,
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
    ModalFooter, FormControl, FormLabel, Input, Select, Spinner, Badge, Tooltip,
    AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader,
    AlertDialogContent, AlertDialogOverlay, Textarea, HStack, VStack, Tag,
    Progress, Center, useBreakpointValue, Checkbox, Link
} from "@chakra-ui/react";
import {
    AddIcon, DeleteIcon, ViewIcon, StarIcon, CheckIcon, CloseIcon,
    LinkIcon, RepeatIcon, SearchIcon, TimeIcon, NotAllowedIcon, DownloadIcon, EditIcon
} from "@chakra-ui/icons";
import { downloadImagem } from "../../utils/downloadImagem";
import EditorImagemModal from "./EditorImagemModal";
import { FiRefreshCw, FiUploadCloud } from "react-icons/fi";
import { RadioGroup, Radio, Stack as ChakraStack } from "@chakra-ui/react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
    getImagensProduto, uploadImagem, importarImagemUrl, aprovarImagem,
    rejeitarImagem, marcarImagemPendente, definirImagemPrincipal, deletarImagem,
    restaurarImagem, reordenarImagens, buscarImagensAutomatico,
    getAnunciosMLProduto, sincronizarImagensML,
    aprovarImagensMassa, rejeitarImagensMassa, excluirImagensMassa,
    bloquearImagem, excluirImagemPermanente, excluirImagensMassaPermanente
} from "../../services/api";
import { atualizarCacheImagem } from "../ProdutoImagem";

const PURGA_DIAS = 30;
const diasParaPurga = (deletedAt) => {
    if (!deletedAt) return null;
    return PURGA_DIAS - Math.floor((Date.now() - new Date(deletedAt)) / 86400000);
};

const STATUS_COLORS = {
    pendente: "yellow",
    aprovada: "green",
    rejeitada: "red",
};

const ORIGEM_LABELS = {
    legado: "Legado",
    fornecedor: "Fornecedor",
    google: "Google",
    marketplace: "Marketplace",
    upload_manual: "Upload Manual",
    alternativo: "Alternativo",
};

const MIMES_ACEITOS = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
const TAMANHO_MAX_MB = 10;

const validarArquivo = (file) => {
    if (!file) return "Nenhum arquivo selecionado.";
    if (!MIMES_ACEITOS.includes(file.type)) {
        return `Formato não suportado (${file.type || "desconhecido"}). Use JPG, PNG ou WEBP.`;
    }
    if (file.size > TAMANHO_MAX_MB * 1024 * 1024) {
        const sizeMb = (file.size / (1024 * 1024)).toFixed(2);
        return `Arquivo muito grande (${sizeMb} MB). Máximo permitido: ${TAMANHO_MAX_MB} MB.`;
    }
    return null;
};

const ProdutoImagensTab = ({ produtoId, onChange }) => {
    const toast = useToast();
    const cancelRef = useRef();
    const fileInputRef = useRef();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const [imagens, setImagens] = useState([]);
    const [loading, setLoading] = useState(true);
    const [uploadQueue, setUploadQueue] = useState([]);
    const [incluirExcluidas, setIncluirExcluidas] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);

    const [previewImagem, setPreviewImagem] = useState(null);
    const [modalUrlOpen, setModalUrlOpen] = useState(false);
    const [urlForm, setUrlForm] = useState({ url: "", origem: "upload_manual", fornecedor_origem: "" });
    const [verOriginal, setVerOriginal] = useState(false);
    const [imagemEditando, setImagemEditando] = useState(null);
    const [importingUrl, setImportingUrl] = useState(false);
    const [buscandoAuto, setBuscandoAuto] = useState(false);
    const [anunciosML, setAnunciosML] = useState({ tem_anuncio: false, total: 0, anuncios: [] });
    const [sincronizando, setSincronizando] = useState(false);
    const [modalSincronizar, setModalSincronizar] = useState(false);
    const [contasSelecionadas, setContasSelecionadas] = useState({});
    const [incluirEcommerce, setIncluirEcommerce] = useState(true);

    const [confirmAlert, setConfirmAlert] = useState({ isOpen: false, type: "", id: null, ids: null });
    const [motivoRejeicao, setMotivoRejeicao] = useState("");
    const [excluirPermanente, setExcluirPermanente] = useState(false);
    const [modalBloqueio, setModalBloqueio] = useState({ isOpen: false, imagemId: null });
    const [bloqueioForm, setBloqueioForm] = useState({ escopo: "produto", motivo: "" });
    const [bloqueando, setBloqueando] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [selecionadas, setSelecionadas] = useState(new Set());
    const [destacarIds, setDestacarIds] = useState(new Set());

    const piscarItens = useCallback((ids) => {
        setDestacarIds(new Set(ids));
        setTimeout(() => setDestacarIds(new Set()), 1500);
    }, []);

    const fetch = useCallback(async (opts = {}) => {
        if (!produtoId) return;
        const silencioso = !!opts.silencioso;
        if (!silencioso) setLoading(true);
        try {
            const res = await getImagensProduto(produtoId);
            const lista = res.data || [];
            const ativas = lista.filter(i => !i.deleted_at && i.status === "aprovada");
            const principal = ativas.find(i => i.principal) || ativas[0];
            atualizarCacheImagem(produtoId, principal ? principal.url : null);
            setImagens(incluirExcluidas ? lista.filter(i => i.deleted_at) : lista.filter(i => !i.deleted_at));
        } catch (e) {
            toast({
                title: "Erro ao carregar imagens",
                description: e.response?.data?.error || e.message,
                status: "error", duration: 4000,
            });
        }
        if (!silencioso) setLoading(false);
    }, [produtoId, incluirExcluidas, toast]);

    useEffect(() => { fetch(); }, [fetch]);

    useEffect(() => {
        if (!produtoId) return;
        getAnunciosMLProduto(produtoId)
            .then(res => setAnunciosML(res.data))
            .catch(() => setAnunciosML({ tem_anuncio: false, total: 0 }));
    }, [produtoId]);

    const notifyChange = (opts = { silencioso: true }) => {
        fetch(opts);
        if (onChange) onChange();
    };

    const handleEnviarArquivos = async (filesArr) => {
        const arquivos = Array.from(filesArr || []);
        if (arquivos.length === 0) return;

        const itens = arquivos.map((file, idx) => ({
            id: `${Date.now()}-${idx}-${file.name}`,
            file,
            nome: file.name,
            progresso: 0,
            status: "aguardando",
            erro: null,
        }));
        setUploadQueue(prev => [...prev, ...itens]);

        for (const item of itens) {
            const erroValidacao = validarArquivo(item.file);
            if (erroValidacao) {
                setUploadQueue(prev => prev.map(p =>
                    p.id === item.id ? { ...p, status: "erro", erro: erroValidacao } : p
                ));
                toast({
                    title: `Falha em ${item.nome}`,
                    description: erroValidacao,
                    status: "warning", duration: 4000,
                });
                continue;
            }

            setUploadQueue(prev => prev.map(p =>
                p.id === item.id ? { ...p, status: "enviando" } : p
            ));

            try {
                await uploadImagem({
                    produto_id: produtoId,
                    arquivo: item.file,
                    onProgress: (pct) => {
                        setUploadQueue(prev => prev.map(p =>
                            p.id === item.id ? { ...p, progresso: pct } : p
                        ));
                    },
                });
                setUploadQueue(prev => prev.map(p =>
                    p.id === item.id ? { ...p, status: "ok", progresso: 100 } : p
                ));
                toast({ title: `${item.nome} enviada`, status: "success", duration: 1800 });
            } catch (err) {
                const msg = err.response?.data?.error || err.message || "Falha desconhecida";
                setUploadQueue(prev => prev.map(p =>
                    p.id === item.id ? { ...p, status: "erro", erro: msg } : p
                ));
                toast({
                    title: `Erro em ${item.nome}`,
                    description: msg,
                    status: "error", duration: 5000,
                });
            }
        }

        notifyChange();
        setTimeout(() => {
            setUploadQueue(prev => prev.filter(p => p.status !== "ok"));
        }, 2500);
    };

    const handleFileSelected = (e) => {
        handleEnviarArquivos(e.target.files);
        e.target.value = "";
    };

    const handleDragOver = (e) => {
        e.preventDefault(); e.stopPropagation();
        setIsDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault(); e.stopPropagation();
        setIsDragOver(false);
    };
    const handleDrop = (e) => {
        e.preventDefault(); e.stopPropagation();
        setIsDragOver(false);
        handleEnviarArquivos(e.dataTransfer.files);
    };

    const handleImportarUrl = async () => {
        if (!urlForm.url) {
            toast({ title: "URL obrigatória", status: "warning", duration: 2000 });
            return;
        }
        setImportingUrl(true);
        try {
            await importarImagemUrl({
                produto_id: produtoId,
                url: urlForm.url,
                origem: urlForm.origem,
                fornecedor_origem: urlForm.fornecedor_origem || null,
            });
            toast({ title: "Imagem importada com sucesso", status: "success", duration: 2500 });
            setModalUrlOpen(false);
            setUrlForm({ url: "", origem: "upload_manual", fornecedor_origem: "" });
            notifyChange();
        } catch (err) {
            toast({
                title: "Erro ao importar",
                description: err.response?.data?.error || err.message || "Falha na importação.",
                status: "error", duration: 5000,
            });
        }
        setImportingUrl(false);
    };

    const abrirModalBloqueio = (imagemId) => {
        setBloqueioForm({ escopo: "produto", motivo: "" });
        setModalBloqueio({ isOpen: true, imagemId });
    };
    const fecharModalBloqueio = () => setModalBloqueio({ isOpen: false, imagemId: null });

    const confirmarBloqueio = async () => {
        setBloqueando(true);
        try {
            const res = await bloquearImagem(modalBloqueio.imagemId, bloqueioForm.escopo, bloqueioForm.motivo || null);
            const copias = res.data?.["cópias_excluidas"] ?? 0;
            toast({
                title: "Imagem bloqueada",
                description: `Escopo: ${bloqueioForm.escopo === "global" ? "global" : "este produto"}${copias > 0 ? ` · ${copias} cópia(s) excluída(s)` : ""}`,
                status: "success", duration: 3500,
            });
            piscarItens([modalBloqueio.imagemId]);
            fecharModalBloqueio();
            notifyChange();
        } catch (err) {
            toast({
                title: "Erro ao bloquear",
                description: err.response?.data?.error || err.message,
                status: "error", duration: 4000,
            });
        }
        setBloqueando(false);
    };

    const abrirModalSincronizar = () => {
        const inicial = {};
        (anunciosML.anuncios || []).forEach((a) => { inicial[a.conta_id] = true; });
        setContasSelecionadas(inicial);
        setIncluirEcommerce(true);
        setModalSincronizar(true);
    };

    const handleSincronizarML = async () => {
        const contasIds = Object.entries(contasSelecionadas)
            .filter(([, v]) => v)
            .map(([k]) => Number(k));

        if (contasIds.length === 0 && !incluirEcommerce) {
            toast({
                title: "Selecione um destino",
                description: "Marque ao menos uma conta do Mercado Livre ou o E-commerce.",
                status: "warning", duration: 3500,
            });
            return;
        }

        setSincronizando(true);
        try {
            const res = await sincronizarImagensML(produtoId, {
                contas_ids: contasIds,
                incluir_ecommerce: incluirEcommerce,
            });
            toast({
                title: "Sincronização agendada",
                description: res.data?.mensagem || "A atualização está sendo processada em segundo plano.",
                status: "success", duration: 4000,
            });
            setModalSincronizar(false);
        } catch (err) {
            toast({
                title: "Erro na sincronização",
                description: err.response?.data?.error || err.message || "Falha ao agendar sincronização.",
                status: "error", duration: 4000,
            });
        }
        setSincronizando(false);
    };

    const handleBuscarAutomatico = async () => {
        setBuscandoAuto(true);
        try {
            const res = await buscarImagensAutomatico(produtoId);
            toast({
                title: "Busca automática enfileirada",
                description: res.data?.mensagem || "A IA vai procurar uma imagem correta nos fornecedores e no Google. Pode levar alguns minutos.",
                status: "info",
                duration: 5000,
            });
            setTimeout(() => notifyChange(), 3000);
        } catch (err) {
            toast({
                title: "Erro ao enfileirar busca",
                description: err.response?.data?.error || err.message || "Falha ao iniciar busca.",
                status: "error", duration: 4000,
            });
        }
        setBuscandoAuto(false);
    };

    const toggleSelecao = (id) => {
        setSelecionadas(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const toggleSelecionarTodas = () => {
        const todas = imagens.map(i => i.id);
        if (selecionadas.size === todas.length) setSelecionadas(new Set());
        else setSelecionadas(new Set(todas));
    };

    const idsAtivas   = Array.from(selecionadas).filter(id => imagens.find(i => i.id === id && !i.deleted_at));
    const idsDeletadas = Array.from(selecionadas).filter(id => imagens.find(i => i.id === id &&  i.deleted_at));

    const abrirConfirm = (type, id = null, ids = null) => {
        setMotivoRejeicao("");
        setExcluirPermanente(false);
        setConfirmAlert({ isOpen: true, type, id, ids });
    };
    const fecharConfirm = () => setConfirmAlert({ isOpen: false, type: "", id: null, ids: null });

    const executarConfirm = async () => {
        setProcessing(true);
        try {
            const { type, id, ids } = confirmAlert;
            if (type === "aprovar")    await aprovarImagem(id);
            if (type === "rejeitar")   await rejeitarImagem(id, motivoRejeicao || null);
            if (type === "pendente")   await marcarImagemPendente(id);
            if (type === "deletar")          { if (excluirPermanente) { await excluirImagemPermanente(id); } else { await deletarImagem(id); } }
            if (type === "deletarPermanente") { await excluirImagemPermanente(id); }
            if (type === "restaurar")  await restaurarImagem(id);
            if (type === "principal")  await definirImagemPrincipal(id);
            if (type === "aprovarMassa")  { const r = await aprovarImagensMassa(ids); toast({ title: `${r.data.atualizadas} imagens aprovadas`, status: "success", duration: 2500 }); piscarItens(ids); setSelecionadas(new Set()); fecharConfirm(); notifyChange(); setProcessing(false); return; }
            if (type === "rejeitarMassa") { const r = await rejeitarImagensMassa(ids, motivoRejeicao || null); toast({ title: `${r.data.rejeitadas} imagens rejeitadas`, status: "success", duration: 2500 }); piscarItens(ids); setSelecionadas(new Set()); fecharConfirm(); notifyChange(); setProcessing(false); return; }
            if (type === "excluirMassa")  { const r = await excluirImagensMassa(ids); toast({ title: `${r.data.excluidas} imagens excluídas`, status: "success", duration: 2500 }); piscarItens(ids); setSelecionadas(new Set()); fecharConfirm(); notifyChange(); setProcessing(false); return; }
            if (type === "excluirMassaPermanente") { const r = await excluirImagensMassaPermanente(ids); toast({ title: `${r.data.purgadas} imagens excluídas permanentemente`, status: "success", duration: 2500 }); piscarItens(ids); setSelecionadas(new Set()); fecharConfirm(); notifyChange(); setProcessing(false); return; }
            const labels = {
                aprovar: "aprovada", rejeitar: "rejeitada", pendente: "marcada como pendente",
                deletar: "excluída", deletarPermanente: "excluída permanentemente", restaurar: "restaurada", principal: "definida como principal"
            };
            toast({ title: `Imagem ${labels[type]}`, status: "success", duration: 2000 });
            piscarItens([id]);
            fecharConfirm();
            notifyChange();
        } catch (err) {
            toast({
                title: "Erro",
                description: err.response?.data?.error || err.message || "Falha na operação",
                status: "error", duration: 4000,
            });
        }
        setProcessing(false);
    };

    const handleDragEnd = async (result) => {
        if (!result.destination || result.destination.index === result.source.index) return;
        const reordered = Array.from(imagens);
        const [removed] = reordered.splice(result.source.index, 1);
        reordered.splice(result.destination.index, 0, removed);
        setImagens(reordered);
        try {
            await reordenarImagens(reordered.map((img, idx) => ({ id: img.id, ordem: idx })));
            toast({ title: "Ordem atualizada", status: "success", duration: 1500 });
        } catch (err) {
            toast({
                title: "Erro ao reordenar",
                description: err.response?.data?.error || err.message,
                status: "error", duration: 3000,
            });
            fetch();
        }
    };

    const renderConfirmConteudo = () => {
        const { type, ids } = confirmAlert;
        const exigeMotivo = type === "rejeitar" || type === "rejeitarMassa";
        const labels = {
            aprovar: "Tem certeza que deseja aprovar esta imagem?",
            rejeitar: "Tem certeza que deseja rejeitar esta imagem?",
            pendente: "Voltar esta imagem para o status pendente? (limpa motivo de rejeição)",
            deletar: excluirPermanente
                ? "Excluir PERMANENTEMENTE? O arquivo físico será removido e não haverá restauração."
                : "Excluir esta imagem? (você poderá restaurar depois)",
            deletarPermanente: "Excluir PERMANENTEMENTE? O arquivo físico será removido e não haverá restauração.",
            restaurar: "Restaurar esta imagem?",
            principal: "Definir como imagem principal do produto?",
            aprovarMassa: `Aprovar ${ids?.length || 0} imagens selecionadas?`,
            rejeitarMassa: `Rejeitar ${ids?.length || 0} imagens selecionadas?`,
            excluirMassa: `Excluir ${ids?.length || 0} imagens selecionadas? (poderá restaurar depois)`,
            excluirMassaPermanente: `Excluir PERMANENTEMENTE ${ids?.length || 0} imagem(ns)? Arquivos físicos serão removidos sem possibilidade de restauração.`,
        };
        return (
            <>
                <Text mb={(exigeMotivo || type === "deletar") ? 3 : 0} fontSize="sm"
                    color={(excluirPermanente && type === "deletar") || type === "deletarPermanente" ? "red.600" : undefined}>
                    {labels[type]}
                </Text>
                {type === "deletar" && (
                    <Checkbox
                        isChecked={excluirPermanente}
                        onChange={(e) => setExcluirPermanente(e.target.checked)}
                        colorScheme="red" size="sm">
                        <Text fontSize="sm">Apagar permanentemente (sem possibilidade de restauração)</Text>
                    </Checkbox>
                )}
                {exigeMotivo && (
                    <Textarea value={motivoRejeicao}
                        onChange={(e) => setMotivoRejeicao(e.target.value)}
                        placeholder="Motivo da rejeição (opcional)"
                        size="sm" />
                )}
            </>
        );
    };

    if (!produtoId) {
        return <Text color="gray.500">Selecione um produto primeiro.</Text>;
    }

    return (
        <Box>
            <Flex justify="space-between" align={{ base: "stretch", md: "center" }} mb={3}
                gap={2} direction={{ base: "column", md: "row" }} wrap="wrap">
                <HStack alignSelf={{ base: "flex-start", md: "center" }}>
                    <Text fontSize="sm" color="gray.700" whiteSpace="nowrap">
                        {imagens.length} {imagens.length === 1 ? "imagem" : "imagens"}
                    </Text>
                    <Button size="xs" variant={incluirExcluidas ? "solid" : "outline"}
                        colorScheme="gray"
                        onClick={() => setIncluirExcluidas(v => !v)}>
                        {incluirExcluidas ? "Ocultar excluídas" : "Mostrar excluídas"}
                    </Button>
                </HStack>
                <Flex wrap="wrap" gap={2}
                    w={{ base: "100%", md: "auto" }}
                    justify={{ base: "stretch", md: "flex-end" }}>
                    <Tooltip label="Reenviar as imagens aprovadas para Mercado Livre e/ou E-commerce" hasArrow>
                        <Button size="sm" leftIcon={<FiRefreshCw />} colorScheme="green"
                            flex={{ base: "1 1 45%", md: "0 0 auto" }}
                            onClick={abrirModalSincronizar}>
                            Sincronizar
                        </Button>
                    </Tooltip>
                    <Tooltip label="Busca em servidores TAGA, produtos com mesmo código global e Google" hasArrow>
                        <Button size="sm" leftIcon={<SearchIcon />} colorScheme="blue" variant="outline"
                            flex={{ base: "1 1 45%", md: "0 0 auto" }}
                            isLoading={buscandoAuto} onClick={handleBuscarAutomatico}>
                            {isMobile ? "Buscar" : "Buscar automático"}
                        </Button>
                    </Tooltip>
                    <Button size="sm" leftIcon={<AddIcon />} colorScheme="purple"
                        flex={{ base: "1 1 45%", md: "0 0 auto" }}
                        onClick={() => fileInputRef.current?.click()}>
                        Upload
                    </Button>
                    <Button size="sm" leftIcon={<LinkIcon />} variant="outline"
                        flex={{ base: "1 1 45%", md: "0 0 auto" }}
                        onClick={() => setModalUrlOpen(true)}>
                        {isMobile ? "URL" : "Importar URL"}
                    </Button>
                    <IconButton size="sm" icon={<RepeatIcon />} aria-label="Atualizar" onClick={fetch} />
                </Flex>
            </Flex>

            <input type="file" hidden multiple ref={fileInputRef} onChange={handleFileSelected}
                accept="image/jpeg,image/jpg,image/png,image/webp" />

            {imagens.length > 0 && (
                <Flex bg={selecionadas.size > 0 ? "purple.50" : "gray.50"}
                    p={3} borderRadius="md" mb={3}
                    align={{ base: "stretch", md: "center" }}
                    justify="space-between"
                    direction={{ base: "column", md: "row" }} gap={2}
                    border="1px solid"
                    borderColor={selecionadas.size > 0 ? "purple.200" : "gray.200"}
                    transition="all 0.15s">
                    <HStack>
                        <Checkbox size="lg" colorScheme="purple"
                            isChecked={selecionadas.size > 0 && selecionadas.size === imagens.length}
                            isIndeterminate={selecionadas.size > 0 && selecionadas.size < imagens.length}
                            onChange={toggleSelecionarTodas}>
                            <Text fontSize="sm" fontWeight="medium" ml={1}>
                                {selecionadas.size === 0
                                    ? "Selecionar todas"
                                    : `${selecionadas.size} selecionada${selecionadas.size === 1 ? "" : "s"}`}
                            </Text>
                        </Checkbox>
                    </HStack>
                    <Flex flexWrap="wrap" gap={2}>
                        <Button size="sm" colorScheme="green" leftIcon={<CheckIcon />}
                            isDisabled={idsAtivas.length === 0}
                            onClick={() => abrirConfirm("aprovarMassa", null, idsAtivas)}>
                            Aprovar
                        </Button>
                        <Button size="sm" colorScheme="red" leftIcon={<CloseIcon />}
                            isDisabled={idsAtivas.length === 0}
                            onClick={() => abrirConfirm("rejeitarMassa", null, idsAtivas)}>
                            Rejeitar
                        </Button>
                        <Button size="sm" variant="outline" colorScheme="red" leftIcon={<DeleteIcon />}
                            isDisabled={idsAtivas.length === 0}
                            onClick={() => abrirConfirm("excluirMassa", null, idsAtivas)}>
                            Excluir
                        </Button>
                        {idsDeletadas.length > 0 && (
                            <Button size="sm" colorScheme="red" leftIcon={<DeleteIcon />}
                                onClick={() => abrirConfirm("excluirMassaPermanente", null, idsDeletadas)}>
                                Excluir definitivamente ({idsDeletadas.length})
                            </Button>
                        )}
                        {selecionadas.size > 0 && (
                            <Button size="sm" variant="ghost" onClick={() => setSelecionadas(new Set())}>
                                Limpar
                            </Button>
                        )}
                    </Flex>
                </Flex>
            )}

            <Box
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                border="2px dashed"
                borderColor={isDragOver ? "purple.400" : "gray.200"}
                bg={isDragOver ? "purple.50" : "transparent"}
                borderRadius="md"
                p={isDragOver ? 4 : 2}
                mb={3}
                transition="all 0.15s"
                cursor="pointer"
                onClick={() => fileInputRef.current?.click()}
                _hover={{ borderColor: "purple.300", bg: "gray.50" }}>
                <Flex align="center" justify="center" gap={2} color="gray.600">
                    <FiUploadCloud size={isDragOver ? 24 : 18} />
                    <Text fontSize="sm">
                        {isDragOver
                            ? "Solte aqui para enviar"
                            : "Arraste imagens para enviar ou clique aqui"}
                    </Text>
                </Flex>
            </Box>

            {uploadQueue.length > 0 && (
                <VStack align="stretch" spacing={2} mb={3} bg="gray.50" p={3} borderRadius="md">
                    <Text fontSize="xs" fontWeight="semibold" color="gray.700">
                        Enviando ({uploadQueue.filter(u => u.status === "enviando" || u.status === "aguardando").length} pendente(s))
                    </Text>
                    {uploadQueue.map(item => (
                        <Box key={item.id}>
                            <Flex justify="space-between" align="center" mb={1}>
                                <Text fontSize="xs" noOfLines={1} maxW="70%">{item.nome}</Text>
                                <Text fontSize="xs"
                                    color={item.status === "erro" ? "red.500" : item.status === "ok" ? "green.500" : "gray.600"}>
                                    {item.status === "erro" ? "Erro" :
                                     item.status === "ok" ? "Concluído" :
                                     item.status === "enviando" ? `${item.progresso}%` : "Aguardando"}
                                </Text>
                            </Flex>
                            <Progress
                                value={item.status === "erro" ? 100 : item.progresso}
                                size="xs"
                                colorScheme={item.status === "erro" ? "red" : item.status === "ok" ? "green" : "purple"}
                                hasStripe={item.status === "enviando"} isAnimated={item.status === "enviando"} />
                            {item.erro && (
                                <Text fontSize="xs" color="red.500" mt={1}>{item.erro}</Text>
                            )}
                        </Box>
                    ))}
                </VStack>
            )}

            {loading ? (
                <Center h="200px">
                    <Spinner size="xl" color="orange.500" />
                </Center>
            ) : imagens.length === 0 ? (
                <Flex direction="column" justify="center" align="center" h="200px"
                    bg="gray.50" borderRadius="md">
                    <Text color="gray.500" mb={3}>Nenhuma imagem cadastrada para este produto.</Text>
                    <Button size="sm" leftIcon={<AddIcon />} colorScheme="purple"
                        onClick={() => fileInputRef.current?.click()}>
                        Enviar primeira imagem
                    </Button>
                </Flex>
            ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <Droppable droppableId="imagens" direction="horizontal">
                        {(provided) => (
                            <SimpleGrid columns={{ base: 2, sm: 3, md: 3, lg: 4 }} spacing={3}
                                ref={provided.innerRef} {...provided.droppableProps}>
                                {imagens.map((img, idx) => (
                                    <Draggable key={img.id} draggableId={String(img.id)} index={idx}
                                        isDragDisabled={!!img.deleted_at}>
                                        {(prov, snapshot) => (
                                            <Box
                                                ref={prov.innerRef}
                                                {...prov.draggableProps}
                                                {...prov.dragHandleProps}
                                                bg={img.deleted_at ? "red.50" : "white"}
                                                border="1px solid"
                                                borderColor={img.deleted_at ? "red.300" : (destacarIds.has(img.id) ? "green.400" : (img.principal ? "yellow.400" : "gray.200"))}
                                                borderRadius="md"
                                                overflow="hidden"
                                                boxShadow={snapshot.isDragging ? "lg" : (destacarIds.has(img.id) ? "0 0 0 3px rgba(72,187,120,0.35)" : "sm")}
                                                transition="all 0.4s ease">
                                                <Box position="relative" pb="100%" bg="gray.50" cursor="pointer"
                                                    onClick={() => { setVerOriginal(false); setPreviewImagem(img); }}>
                                                    <Image src={img.url} alt={`#${img.id}`}
                                                        position="absolute" top={0} left={0}
                                                        w="100%" h="100%" objectFit="contain"
                                                        loading="lazy"
                                                        fallbackSrc="https://via.placeholder.com/200?text=?" />
                                                    <HStack position="absolute" top={1} left={1} spacing={1}
                                                        onClick={(e) => e.stopPropagation()}>
                                                        <Box bg="white" borderRadius="md" px={1} py={1} boxShadow="md">
                                                            <Checkbox size="lg"
                                                                colorScheme={img.deleted_at ? "red" : "purple"}
                                                                isChecked={selecionadas.has(img.id)}
                                                                onChange={() => toggleSelecao(img.id)} />
                                                        </Box>
                                                        {img.principal && (
                                                            <Tag size="xs" colorScheme="yellow" px={1}>
                                                                <StarIcon mr={0.5} boxSize={2} /> Princ.
                                                            </Tag>
                                                        )}
                                                        {img.url_original && (
                                                            <Tooltip label="Fundo removido. A foto original está guardada, clique para ver" hasArrow>
                                                                <Tag size="xs" colorScheme="purple" px={1}>original</Tag>
                                                            </Tooltip>
                                                        )}
                                                    </HStack>
                                                    {img.deleted_at ? (
                                                        <Badge colorScheme="red"
                                                            position="absolute" bottom={1} left="50%"
                                                            transform="translateX(-50%)"
                                                            fontSize="xs" px={2}>
                                                            Excluída
                                                        </Badge>
                                                    ) : (
                                                        <Badge colorScheme={STATUS_COLORS[img.status]}
                                                            position="absolute" top={1} right={1}>
                                                            {img.status}
                                                        </Badge>
                                                    )}
                                                </Box>
                                                <VStack p={2} spacing={1} align="stretch">
                                                    <Flex justify="space-between" align="center" fontSize="xs">
                                                        <Text color="gray.600" noOfLines={1}>
                                                            {ORIGEM_LABELS[img.origem] || img.origem}
                                                        </Text>
                                                        <Text color="gray.500">#{img.id}</Text>
                                                    </Flex>
                                                    <HStack justify="space-between" spacing={1} flexWrap="wrap">
                                                        <Tooltip label="Ver" hasArrow>
                                                            <IconButton size="xs" icon={<ViewIcon />}
                                                                onClick={() => { setVerOriginal(false); setPreviewImagem(img); }}
                                                                aria-label="Ver" />
                                                        </Tooltip>
                                                        {!img.deleted_at && (
                                                            <Tooltip label="Editar: recortar, girar e remover fundo" hasArrow>
                                                                <IconButton size="xs" colorScheme="purple" variant="outline"
                                                                    icon={<EditIcon />}
                                                                    onClick={() => setImagemEditando(img)}
                                                                    aria-label="Editar" />
                                                            </Tooltip>
                                                        )}
                                                        {img.deleted_at ? (
                                                            <>
                                                                {(() => { const d = diasParaPurga(img.deleted_at); return d !== null && (
                                                                    <Text fontSize="9px" color={d <= 3 ? "red.600" : "orange.500"} whiteSpace="nowrap" w="100%" textAlign="center">
                                                                        {d <= 0 ? "Remoção pendente" : `Remove em ${d}d`}
                                                                    </Text>
                                                                ); })()}
                                                                <Tooltip label="Restaurar" hasArrow>
                                                                    <IconButton size="xs" colorScheme="blue" icon={<RepeatIcon />}
                                                                        onClick={() => abrirConfirm("restaurar", img.id)}
                                                                        aria-label="Restaurar" />
                                                                </Tooltip>
                                                                <Tooltip label="Excluir definitivamente" hasArrow>
                                                                    <IconButton size="xs" colorScheme="red" icon={<DeleteIcon />}
                                                                        onClick={() => abrirConfirm("deletarPermanente", img.id)}
                                                                        aria-label="Excluir permanentemente" />
                                                                </Tooltip>
                                                            </>
                                                        ) : (
                                                            <>
                                                                {img.status !== "aprovada" && (
                                                                    <Tooltip label="Aprovar" hasArrow>
                                                                        <IconButton size="xs" colorScheme="green" icon={<CheckIcon />}
                                                                            onClick={() => abrirConfirm("aprovar", img.id)}
                                                                            aria-label="Aprovar" />
                                                                    </Tooltip>
                                                                )}
                                                                {img.status !== "rejeitada" && (
                                                                    <Tooltip label="Rejeitar" hasArrow>
                                                                        <IconButton size="xs" colorScheme="red" icon={<CloseIcon />}
                                                                            onClick={() => abrirConfirm("rejeitar", img.id)}
                                                                            aria-label="Rejeitar" />
                                                                    </Tooltip>
                                                                )}
                                                                {img.status !== "pendente" && (
                                                                    <Tooltip label="Marcar como pendente" hasArrow>
                                                                        <IconButton size="xs" colorScheme="orange" variant="outline" icon={<TimeIcon />}
                                                                            onClick={() => abrirConfirm("pendente", img.id)}
                                                                            aria-label="Pendente" />
                                                                    </Tooltip>
                                                                )}
                                                                {img.status === "aprovada" && !img.principal && (
                                                                    <Tooltip label="Definir principal" hasArrow>
                                                                        <IconButton size="xs" colorScheme="yellow" icon={<StarIcon />}
                                                                            onClick={() => abrirConfirm("principal", img.id)}
                                                                            aria-label="Principal" />
                                                                    </Tooltip>
                                                                )}
                                                                <Tooltip label="Bloquear (impede reuso desta imagem)" hasArrow>
                                                                    <IconButton size="xs" colorScheme="red" variant="ghost" icon={<NotAllowedIcon />}
                                                                        onClick={() => abrirModalBloqueio(img.id)}
                                                                        aria-label="Bloquear" />
                                                                </Tooltip>
                                                                <Tooltip label="Excluir" hasArrow>
                                                                    <IconButton size="xs" variant="ghost" icon={<DeleteIcon />}
                                                                        onClick={() => abrirConfirm("deletar", img.id)}
                                                                        aria-label="Excluir" />
                                                                </Tooltip>
                                                            </>
                                                        )}
                                                    </HStack>
                                                </VStack>
                                            </Box>
                                        )}
                                    </Draggable>
                                ))}
                                {provided.placeholder}
                            </SimpleGrid>
                        )}
                    </Droppable>
                </DragDropContext>
            )}

            <Modal isOpen={!!previewImagem} onClose={() => setPreviewImagem(null)}
                size={{ base: "full", md: "xl" }} scrollBehavior="inside">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader fontSize="md">Imagem #{previewImagem?.id}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {previewImagem && (
                            <>
                                <Flex justify="center" mb={2}>
                                    <Image src={verOriginal && previewImagem.url_original ? previewImagem.url_original : previewImagem.url}
                                        maxH={{ base: "300px", md: "500px" }}
                                        objectFit="contain" />
                                </Flex>
                                {previewImagem.url_original && (
                                    <Flex justify="center" align="center" gap={2} mb={4} flexWrap="wrap">
                                        <Button size="xs" variant={verOriginal ? "outline" : "solid"} colorScheme="purple"
                                            onClick={() => setVerOriginal(false)}>
                                            Foto publicada
                                        </Button>
                                        <Button size="xs" variant={verOriginal ? "solid" : "outline"} colorScheme="purple"
                                            onClick={() => setVerOriginal(true)}>
                                            Foto original (com fundo)
                                        </Button>
                                    </Flex>
                                )}
                                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={2} fontSize="sm">
                                    <Box><b>Status:</b> <Badge colorScheme={STATUS_COLORS[previewImagem.status]}>{previewImagem.status}</Badge></Box>
                                    <Box><b>Origem:</b> {ORIGEM_LABELS[previewImagem.origem]}</Box>
                                    <Box><b>Principal:</b> {previewImagem.principal ? "Sim" : "Não"}</Box>
                                    <Box><b>Ordem:</b> {previewImagem.ordem}</Box>
                                    <Box><b>Dimensões:</b> {previewImagem.width}×{previewImagem.height}</Box>
                                    <Box><b>Tamanho:</b> {previewImagem.bytes ? `${Math.round(previewImagem.bytes / 1024)} KB` : "-"}</Box>
                                    <Box gridColumn={{ md: "span 2" }} wordBreak="break-all">
                                        <b>URL:</b> <Text as="span" fontSize="xs">{previewImagem.url}</Text>
                                    </Box>
                                    {previewImagem.url_original && (
                                        <Box gridColumn={{ md: "span 2" }} wordBreak="break-all">
                                            <b>Foto original:</b>{' '}
                                            <Link href={previewImagem.url_original} isExternal color="purple.600" fontSize="xs">
                                                {previewImagem.url_original}
                                            </Link>
                                        </Box>
                                    )}
                                    {previewImagem.origem_url && (
                                        <Box gridColumn={{ md: "span 2" }} wordBreak="break-all">
                                            <b>Origem URL:</b> <Text as="span" fontSize="xs">{previewImagem.origem_url}</Text>
                                        </Box>
                                    )}
                                    {previewImagem.motivo_rejeicao && (
                                        <Box gridColumn={{ md: "span 2" }}>
                                            <b>Motivo rejeição:</b> {previewImagem.motivo_rejeicao}
                                        </Box>
                                    )}
                                </SimpleGrid>
                            </>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        {previewImagem && !previewImagem.deleted_at && (
                            <Button size="sm" colorScheme="purple" variant="outline" mr="auto"
                                leftIcon={<EditIcon />}
                                onClick={() => { const alvo = previewImagem; setPreviewImagem(null); setImagemEditando(alvo); }}>
                                Editar imagem
                            </Button>
                        )}
                        <Button leftIcon={<DownloadIcon />} colorScheme="blue" variant="outline" mr={2}
                            onClick={() => downloadImagem(previewImagem?.url, previewImagem?.nome)}>
                            Baixar
                        </Button>
                        <Button onClick={() => setPreviewImagem(null)}>Fechar</Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={modalSincronizar} onClose={() => setModalSincronizar(false)}
                size={{ base: "sm", md: "md" }} isCentered>
                <ModalOverlay />
                <ModalContent mx={3}>
                    <ModalHeader fontSize="md">Sincronizar imagens</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <Text fontSize="sm" color="gray.600" mb={3}>
                            Selecione os destinos onde as imagens aprovadas serão sincronizadas:
                        </Text>
                        <VStack align="stretch" spacing={3}>
                            {anunciosML.tem_anuncio && (
                                <Box>
                                    <Text fontSize="sm" fontWeight="semibold" mb={2}>Mercado Livre</Text>
                                    <VStack align="stretch" spacing={1.5} pl={2}>
                                        {(anunciosML.anuncios || []).map((a) => (
                                            <Checkbox key={a.conta_id}
                                                isChecked={!!contasSelecionadas[a.conta_id]}
                                                onChange={(e) => setContasSelecionadas((prev) => ({
                                                    ...prev, [a.conta_id]: e.target.checked
                                                }))}>
                                                <Text fontSize="sm">
                                                    {a.conta_nome || `Conta ${a.conta_id}`}
                                                    <Text as="span" fontSize="xs" color="gray.500" ml={2}>
                                                        ({a.id_ml})
                                                    </Text>
                                                </Text>
                                            </Checkbox>
                                        ))}
                                    </VStack>
                                </Box>
                            )}
                            <Box>
                                <Text fontSize="sm" fontWeight="semibold" mb={2}>E-commerce</Text>
                                <Checkbox pl={2}
                                    isChecked={incluirEcommerce}
                                    onChange={(e) => setIncluirEcommerce(e.target.checked)}>
                                    <Text fontSize="sm">Site TAGA Autopartes</Text>
                                </Checkbox>
                            </Box>
                            {!anunciosML.tem_anuncio && (
                                <Text fontSize="xs" color="gray.500">
                                    Este produto não possui anúncios no Mercado Livre.
                                </Text>
                            )}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="ghost" mr={2} onClick={() => setModalSincronizar(false)}>
                            Cancelar
                        </Button>
                        <Button colorScheme="green" leftIcon={<FiRefreshCw />}
                            isLoading={sincronizando} onClick={handleSincronizarML}>
                            Sincronizar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={modalUrlOpen} onClose={() => setModalUrlOpen(false)}
                size={{ base: "sm", md: "md" }} isCentered>
                <ModalOverlay />
                <ModalContent mx={3}>
                    <ModalHeader fontSize="md">Importar imagem de URL</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={3} align="stretch">
                            <FormControl isRequired>
                                <FormLabel fontSize="sm">URL da imagem</FormLabel>
                                <Input size="sm" placeholder="https://..."
                                    value={urlForm.url}
                                    onChange={(e) => setUrlForm({ ...urlForm, url: e.target.value })} />
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Origem</FormLabel>
                                <Select size="sm" value={urlForm.origem}
                                    onChange={(e) => setUrlForm({ ...urlForm, origem: e.target.value })}>
                                    {Object.entries(ORIGEM_LABELS).filter(([v]) => v !== "legado").map(([v, l]) => (
                                        <option key={v} value={v}>{l}</option>
                                    ))}
                                </Select>
                            </FormControl>
                            {urlForm.origem === "fornecedor" && (
                                <FormControl>
                                    <FormLabel fontSize="sm">Nome do fornecedor</FormLabel>
                                    <Input size="sm" placeholder="Ex: MORELATE"
                                        value={urlForm.fornecedor_origem}
                                        onChange={(e) => setUrlForm({ ...urlForm, fornecedor_origem: e.target.value })} />
                                </FormControl>
                            )}
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} onClick={() => setModalUrlOpen(false)} isDisabled={importingUrl} size="sm">
                            Cancelar
                        </Button>
                        <Button colorScheme="purple" onClick={handleImportarUrl} isLoading={importingUrl} size="sm">
                            Importar
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={modalBloqueio.isOpen} onClose={fecharModalBloqueio}
                size={{ base: "sm", md: "md" }} isCentered>
                <ModalOverlay />
                <ModalContent mx={3}>
                    <ModalHeader fontSize="md">Bloquear imagem</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack spacing={3} align="stretch">
                            <Text fontSize="sm" color="gray.600">
                                A imagem bloqueada não poderá ser cadastrada novamente (upload, busca automática ou importação). Cópias existentes serão excluídas conforme o escopo.
                            </Text>
                            <FormControl>
                                <FormLabel fontSize="sm">Escopo</FormLabel>
                                <RadioGroup value={bloqueioForm.escopo}
                                    onChange={(v) => setBloqueioForm({ ...bloqueioForm, escopo: v })}>
                                    <ChakraStack direction="column" spacing={2}>
                                        <Radio value="produto" colorScheme="purple">
                                            <Text fontSize="sm">Apenas para este produto</Text>
                                        </Radio>
                                        <Radio value="global" colorScheme="red">
                                            <Text fontSize="sm">Globalmente (em todos os produtos)</Text>
                                        </Radio>
                                    </ChakraStack>
                                </RadioGroup>
                            </FormControl>
                            <FormControl>
                                <FormLabel fontSize="sm">Motivo (opcional)</FormLabel>
                                <Textarea size="sm" rows={3}
                                    placeholder="Ex: imagem genérica, baixa qualidade, conteúdo proibido..."
                                    value={bloqueioForm.motivo}
                                    onChange={(e) => setBloqueioForm({ ...bloqueioForm, motivo: e.target.value })} />
                            </FormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button mr={3} onClick={fecharModalBloqueio} isDisabled={bloqueando} size="sm">
                            Cancelar
                        </Button>
                        <Button colorScheme="red" onClick={confirmarBloqueio} isLoading={bloqueando} size="sm"
                            leftIcon={<NotAllowedIcon />}>
                            Bloquear
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <AlertDialog isOpen={confirmAlert.isOpen} leastDestructiveRef={cancelRef}
                onClose={fecharConfirm} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent mx={3}>
                        <AlertDialogHeader fontSize="md">Confirmar ação</AlertDialogHeader>
                        <AlertDialogBody>{renderConfirmConteudo()}</AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={fecharConfirm} isDisabled={processing} size="sm">
                                Cancelar
                            </Button>
                            <Button colorScheme="purple" onClick={executarConfirm}
                                ml={3} isLoading={processing} size="sm">
                                Confirmar
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>

            <EditorImagemModal
                isOpen={!!imagemEditando}
                imagem={imagemEditando}
                onClose={() => setImagemEditando(null)}
                onSalvo={() => { setImagemEditando(null); fetch(); if (onChange) onChange(); }}
            />
        </Box>
    );
};

export default ProdutoImagensTab;
